import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { isAccessible } from '@/components/AccessControl';
import { BreadcrumbHistory, useBreadcrumbContext } from '@/components/Breadcrumb/context';
import { BREADCRUMB_QUERY_PARAM } from '@/components/Breadcrumb/hooks/useBreadcrumb';
import useNavigation from '@/hooks/useNavigation';
import config from '@/modules/content/config';
import { PathPerformanceFragment } from '@/modules/corporate/modules/path/graphql/fragments/generated/PathPerformance';
import { useLearningPathContentQuery } from '@/modules/corporate/modules/path/graphql/queries/generated/learningPathContent';
import {
  ContentEntity,
  ContentLockSettings,
  PathContentType,
  PerformanceStatusEnum,
  ResourceActions,
  ResourceSubjects,
  ResourceTypeEnum,
} from '@/types/schema';
import { isValidObjectId } from '@/utils/urql';

import usePathContent from './usePathContent';

const availability = (path: PathContentType, contentId: string) => {
  const { userPermissions } = path;
  const canConsume = userPermissions
    ? isAccessible(userPermissions, ResourceActions.Consume, ResourceSubjects.Resource)
    : true;

  if (!canConsume) {
    return false;
  }

  //@ts-ignore  TODO: need to add pathLockSettings in  PathPerformanceType
  const pathPerformance = path?.performance as PathPerformanceFragment;

  const lockSettings =
    pathPerformance?.pathLockSettings?.reduce(
      (acc, lockSetting) => {
        acc[lockSetting.contentId] = lockSetting;
        return acc;
      },
      {} as Record<string, ContentLockSettings>,
    ) ?? {};

  return lockSettings[contentId]?.isAvailable;
};

export const useUpdateContent = (
  parentEntityId: string,
  contentId: string,
  performanceStatus?: PerformanceStatusEnum,
) => {
  const hasValidParentEntityId =
    isValidObjectId(parentEntityId ?? '') && parentEntityId !== contentId;
  const [pathId, setPathId] = useState<string>(parentEntityId);
  const [nextContent, setNextContent] = useState<ContentEntity | null | undefined>(null);
  const navigate = useNavigation();
  const searchParams = useSearchParams();
  const { fetchPathContent } = usePathContent();
  const { setBreadcrumbHistory, breadcrumbUrl } = useBreadcrumbContext();
  const [data, refetch] = useLearningPathContentQuery({
    variables: {
      filters: {
        id: parentEntityId,
      },
    },
    pause: !hasValidParentEntityId,
  });

  useEffect(() => {
    if (performanceStatus == PerformanceStatusEnum.Completed && hasValidParentEntityId) {
      if (parentEntityId) refetch({ requestPolicy: 'network-only' });
    }
  }, [hasValidParentEntityId, parentEntityId, performanceStatus, refetch]);
  const path = data.data?.content as PathContentType | undefined;
  const findNextContent = useCallback(
    async (
      currPath: PathContentType,
      sections: any[],
      currentContentId: string,
    ): Promise<ContentEntity | null | undefined> => {
      const flattenSection = sections.flatMap((section) => section.childResources);

      if (flattenSection.length) {
        const currentIndex = flattenSection.findIndex(
          (content: any) => content.id === currentContentId,
        );
        if (currentIndex === flattenSection.length) {
          return null;
        }

        let upcomingContent = flattenSection[currentIndex + 1];

        const availabile = upcomingContent ? availability(currPath, upcomingContent.id) : false;

        if (!availabile) {
          return null;
        }

        if (upcomingContent?.type === ResourceTypeEnum.Path) {
          setPathId(upcomingContent.id);
          const nestedPathContent = (await fetchPathContent(upcomingContent.id)) as PathContentType;
          const { id: nestedPathId, name: nestedPathName } = nestedPathContent;
          const contentConfig = config[ResourceTypeEnum.Path];
          const { details } = contentConfig.routes;
          const url = details(nestedPathId.toString());
          const query = {
            parentEntityId,
            pathId: nestedPathId.toString(),
          };
          const path = `${url}?parentEntityId=${parentEntityId}`;
          setBreadcrumbHistory((prevBreadcrumbHistory: BreadcrumbHistory) => {
            if (prevBreadcrumbHistory[nestedPathId.toString()]?.id) return prevBreadcrumbHistory;

            return {
              ...prevBreadcrumbHistory,
              [nestedPathId.toString()]: {
                id: nestedPathId.toString(),
                pageTitle: nestedPathName,
                path,
                module: { type: ResourceTypeEnum.Path },
                query,
              },
            } as BreadcrumbHistory;
          });
          upcomingContent =
            nestedPathContent?.sections &&
            (await findNextContent(
              nestedPathContent,
              nestedPathContent?.sections,
              currentContentId,
            ));
        }

        if (upcomingContent) {
          return upcomingContent;
        }
      }
      return null;
    },
    [fetchPathContent],
  );

  const checkParentPathContents = useCallback(
    async (
      parentPaths: string[],
    ): Promise<{ nextContent: ContentEntity; id: string } | null | undefined> => {
      let currPathId = pathId;
      parentPaths.reverse();
      for (const parentId of parentPaths) {
        if (parentId === pathId) continue;
        const parentPathContent = (await fetchPathContent(parentId)) as PathContentType;
        try {
          const nextContent =
            parentPathContent?.sections &&
            (await findNextContent(parentPathContent, parentPathContent.sections, currPathId));
          if (nextContent) {
            return { nextContent, id: parentPathContent.id };
          }
        } catch (error) {
          console.log(error);
        }
        currPathId = parentId;
      }
      return null;
    },
    [fetchPathContent, findNextContent],
  );

  const parseParentPaths = (url: string): string[] => {
    const urlSearchParams = new URLSearchParams(new URL(url).search);
    return urlSearchParams.getAll('bl').filter((pathId) => isValidObjectId(pathId));
  };
  const getPathDetailName = (contentId?: string, contentType?: ResourceTypeEnum) => {
    if (contentId && contentType) {
      const contentConfig = config[contentType];
      const { details } = contentConfig.routes;

      return details(contentId);
    }
  };
  const getPathViewName = (contentId?: string, contentType?: ResourceTypeEnum) => {
    if (contentId && contentType) {
      const contentConfig = config[contentType];
      const { view } = contentConfig.routes;

      return view?.(contentId);
    }
  };
  useEffect(() => {
    const nextContent = async () => {
      if (path?.sections) {
        let nextContent = await findNextContent(path, path.sections, contentId);
        if (!nextContent) {
          const parentPaths = parseParentPaths(window.location.href);
          const item = await checkParentPathContents(parentPaths);
          nextContent = item?.nextContent;
          nextContent && setPathId(item?.id ?? pathId);
        }
        setNextContent(nextContent);
      }
    };

    nextContent();
  }, [
    checkParentPathContents,
    contentId,
    findNextContent,
    path,
    path?.sections,
    performanceStatus,
  ]);

  const updateContentId = (contentId: string, type: ResourceTypeEnum) => {
    const params = new URLSearchParams(searchParams.toString());
    const nextParentEntityIdIndex = breadcrumbUrl?.indexOf(pathId) ?? -1;
    const allBl = breadcrumbUrl?.length ? breadcrumbUrl : [];
    if (nextParentEntityIdIndex == -1) {
      allBl.push(pathId);
    }
    const updatedNextParentEntityIdIndex = allBl.indexOf(pathId);
    const query = {
      ...params,
      parentEntityId: pathId,
      [BREADCRUMB_QUERY_PARAM]: allBl.slice(0, updatedNextParentEntityIdIndex + 1),
    };
    let url;
    switch (type) {
      case ResourceTypeEnum.OfflineEventContent:
      case ResourceTypeEnum.LiveEventContent:
      case ResourceTypeEnum.ExternalEventContent:
      case ResourceTypeEnum.RecurringOfflineEventContent:
      case ResourceTypeEnum.RecurringLiveEventContent:
      case ResourceTypeEnum.RecurringExternalEventContent:
        url = getPathDetailName(contentId, type);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Course:
        url = getPathDetailName(contentId, ResourceTypeEnum.Course);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Path:
        url = getPathDetailName(contentId, ResourceTypeEnum.Path);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Article:
        url = getPathViewName(contentId, ResourceTypeEnum.Article);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Quiz:
        url = getPathDetailName(contentId, ResourceTypeEnum.Quiz);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Survey:
        url = getPathDetailName(contentId, ResourceTypeEnum.Survey);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Exercise:
        url = getPathDetailName(contentId, ResourceTypeEnum.Exercise);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.Task:
        url = getPathDetailName(contentId, ResourceTypeEnum.Task);
        navigate.push(url, { query: query });
        break;
      case ResourceTypeEnum.ExternalContent:
        url = getPathDetailName(contentId, ResourceTypeEnum.ExternalContent);
        navigate.push(url, { query: query });
        break;
    }
  };

  const onNext = nextContent
    ? () => updateContentId(nextContent.id as string, nextContent.type as ResourceTypeEnum)
    : undefined;

  return {
    nextContentName: nextContent?.name,
    onNext,
  };
};
